import axios from 'axios';
import {getPostHeaders} from './headers';
import {RESOURCES as resources} from '../receiptResources';

/**
 * Build the user-facing banner shown when saving a receipt Product fails.
 *
 * `err` is the rejected value from postItem: the backend body
 * ({error, sku, title, product_id}) on an HTTP error, or a string for
 * non-HTTP failures. When the failing item can be identified (by the backend
 * sku/title, or by matching product_id against the local items list) the
 * message names it so the user can go straight to it.
 *
 * @param err rejected value from postItem
 * @param {Array} items local receipt items (carry sku/title/product_id)
 * @return {string}
 */
export const formatReceiptItemSaveError = (err, items = []) => {
    const data = (err && typeof err === 'object') ? err : null;
    const detail = data?.error ?? (typeof err === 'string' ? err : 'Request failed');
    const local = data ? items.find(r => r.product_id === data.product_id) : null;
    const sku = data?.sku || local?.sku || '';
    const title = data?.title || local?.title || '';
    if (sku || title) {
        const label = [sku, title && `(${title})`].filter(Boolean).join(' ');
        return `Sorry, there was an error saving Product ${label}: ${detail}. Please edit this item.`;
    }
    return `Sorry, there was an error saving Products for this Receipt: ${detail}. Please edit this item.`;
};

/**
 * Post a request to Start, Create or Update a Receipt
 *
 * @param type
 * @param data
 * @return {Promise<unknown>}
 */
export const postReceipt = (type, data) => {
    return new Promise(function (resolve, reject) {
        if (data) {

            const onFailure = function (result) {
                let message = result.data?.error ?? result;
                return reject(message);
            }

            const onSuccess = function (result) {
                return resolve(result);
            }

            const params = {
                data: data
            };
            let cfg;
            switch (type) {
                case 'startReceipt':
                    cfg = resources.ReceiptStart.CREATE({id: data.org_id});
                    break;
                case 'createReceipt':
                    cfg = resources.ReceiptCreate.CREATE(params);
                    break;
                case 'updateReceipt':
                    cfg = resources.Receipt.UPDATE(params);
                    break;
                default:
                    break;
            }

            // Set headers
            const headers = getPostHeaders(true);

            try {
                axios({
                    method: cfg.method,
                    url: cfg.uri,
                    data: cfg.data,
                    headers: headers
                })
                    .then((result) => {
                        if (result && result.data) {
                            return onSuccess(result.data);
                        }
                    })
                    .catch((error) => {
                        if (error.message) {
                            onFailure(error.message);
                        } else if (error.response) {
                            // Server response outside 2xx
                            onFailure(error.response);
                        } else if (error.request) {
                            // No response
                            onFailure(error.request);
                        } else {
                            onFailure(error);
                        }
                    });
            } catch (err) {
                console.log('postReceipt try/catch error: ', err);
                onFailure(err);
            }
        } else {
            return resolve();
        }
    });
};

/**
 * Post a request to Add, Update or Delete a Product in a Receipt
 *
 * @param type
 * @param data
 * @return {Promise<unknown>}
 */
export const postItem = (type, data) => {
    return new Promise(function (resolve, reject) {
        if (data) {

            const onFailure = function (result) {
                // Reject with the full backend body ({message, error, product_id,
                // sku, title}) when available so callers can identify the failing
                // item; falls back to a string for non-HTTP failures.
                return reject(result?.data ?? result);
            }

            const onSuccess = function (result) {
                return resolve(result);
            }

            let productToPost = {
                receipt_id: data.receipt_id,
                product_id: data.product_id,
                qty: data.qty,
                num_boxes: data.num_boxes,
                notes: data.notes
            };
            if (type === 'updateItem') {
                productToPost.id = data.id;
                productToPost.new_qty = data.new_qty;
            }
            if (type === 'updateNotes') {
                productToPost.id = data.id;
            }
            if (type === 'deleteItem') {
                productToPost.id = data.id;
            }
            const params = {
                data: productToPost
            };
            let cfg;
            switch (type) {
                case 'addItem':
                    cfg = resources.ReceiptItem.CREATE(params);
                    break;
                case 'updateItem':
                    cfg = resources.ReceiptItem.UPDATE(params);
                    break;
                case 'updateNotes':
                    cfg = resources.ReceiptItemNotes.UPDATE(params);
                    break;
                case 'deleteItem':
                    cfg = resources.ReceiptItem.DELETE(params);
                    break;
                default:
                    break;
            }

            // Set headers
            const headers = getPostHeaders(true);

            try {
                axios({
                    method: cfg.method,
                    url: cfg.uri,
                    data: cfg.data,
                    headers: headers
                })
                    .then((result) => {
                        if (result && result.data) {
                            return onSuccess(result.data);
                        }
                    })
                    .catch((error) => {
                        if (error.response) {
                            // Server response outside 2xx — pass the full response
                            // so onFailure can surface error.response.data.
                            onFailure(error.response);
                        } else if (error.request) {
                            // No response
                            onFailure(error.request);
                        } else {
                            onFailure(error.message ?? error);
                        }
                    });
            } catch (err) {
                console.log('postItem try/catch error: ', err);
                onFailure((err.message) ? err.message : err);
            }
        } else {
            return resolve();
        }
    });
};
